/**
 * Ordering Element Player
 * Displays a question and a vertical list of items that users reorder via drag-and-drop.
 * Items are randomized on initial render. Uses @dnd-kit sortable with animations.
 */

import * as React from 'react';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    TouchSensor,
    KeyboardSensor,
    SortableContext,
    SortableItem,
    closestCenter,
    useSensor,
    useSensors,
    arrayMove,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    cn,
    Badge,
    shuffleArray,
    type DragStartEvent,
    type DragEndEvent,
} from '@ai-content-hub/content-kit';
import type { ElementPlayerProps, GradingResult, BehaviorConfig } from '@ai-content-hub/content-kit';
import { GripVertical, Check, X as XIcon } from 'lucide-react';
import {
    type OrderingElementData,
    type OrderingAnswer,
    type OrderingItem,
    normalizeOrderingData,
    getCorrectOrder,
    orderingElementSchema,
} from './schema';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { RichTextInline } from '../block';

export interface OrderingPlayerProps extends ElementPlayerProps<OrderingElementData, OrderingAnswer> {}

function SortableOrderingItem({
    item,
    index,
    disabled,
    itemState,
    correctPosition,
    showCorrectAnswer,
    t,
}: {
    item: OrderingItem;
    index: number;
    disabled: boolean;
    itemState: 'default' | 'correct' | 'incorrect';
    correctPosition?: number;
    showCorrectAnswer: boolean;
    t: (key: string, fallback?: string) => string;
}) {
    return (
        <SortableItem id={item.id} disabled={disabled}>
            {({ setNodeRef, style, isDragging, attributes, listeners }) => (
                <div
                    ref={setNodeRef}
                    style={style}
                    className={cn(
                        'ordering-player-item',
                        'flex items-center gap-3 rounded-lg border bg-card p-3 transition-all duration-200',
                        !disabled && 'hover:shadow-sm',
                        isDragging && 'opacity-60 scale-[0.98] shadow-lg z-50 ring-2 ring-primary/40',
                        itemState === 'correct' && 'border-success/60 bg-success/5',
                        itemState === 'incorrect' && 'border-destructive/60 bg-destructive/5',
                        itemState === 'default' && 'border-border',
                        // EAA-1680: playful-skin pressable-row hooks — see
                        // docs/PLAN-Playful-Interactive-Options.md Phase 4c.
                        'content-option',
                        disabled && 'content-option--static',
                        isDragging && 'content-option--dragging',
                        itemState === 'correct' && 'content-feedback--correct',
                        itemState === 'incorrect' && 'content-feedback--incorrect',
                    )}
                >
                    {/* Drag handle */}
                    <div
                        className={cn(
                            'ordering-player-item-handle flex items-center justify-center shrink-0 touch-none',
                            disabled ? 'cursor-default text-muted-foreground/30' : 'cursor-grab active:cursor-grabbing text-muted-foreground',
                        )}
                        {...listeners}
                        {...attributes}
                        role="button"
                        aria-label={t('dragToReorder')}
                    >
                        <GripVertical className="h-5 w-5" />
                    </div>

                    {/* Position badge */}
                    <Badge
                        variant="secondary"
                        className={cn(
                            'ordering-player-item-badge shrink-0 w-7 justify-center font-semibold',
                            itemState === 'correct' && 'bg-success/15 text-success',
                            itemState === 'incorrect' && 'bg-destructive/15 text-destructive',
                            itemState === 'default' && 'bg-muted text-muted-foreground',
                        )}
                    >
                        {index + 1}
                    </Badge>

                    {/* Item text */}
                    <span className="ordering-player-item-text flex-1 text-sm font-medium text-foreground">
                        <RichTextInline text={item.text} />
                    </span>

                    {/* Status icon */}
                    {itemState === 'correct' && (
                        <div className="ordering-player-item-status flex items-center gap-1 animate-in fade-in duration-200">
                            <Check className="h-4 w-4 text-success" />
                        </div>
                    )}
                    {itemState === 'incorrect' && (
                        <div className="ordering-player-item-status flex items-center gap-1 animate-in fade-in duration-200">
                            <XIcon className="h-4 w-4 text-destructive" />
                            {showCorrectAnswer && correctPosition != null && (
                                <Badge
                                    variant="secondary"
                                    className="bg-success/15 text-success text-xs"
                                >
                                    →{correctPosition}
                                </Badge>
                            )}
                        </div>
                    )}
                </div>
            )}
        </SortableItem>
    );
}

export const OrderingPlayer: React.FC<OrderingPlayerProps> = ({
    data,
    answer,
    onAnswer,
    disabled = false,
    showFeedback = false,
    result,
    isReview = false,
    className,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? orderingElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const orderingData = React.useMemo(() => normalizeOrderingData(data || {}), [data]);
    const { items, question } = orderingData;

    const showCorrectAnswer =
        (config?.behavior as BehaviorConfig | undefined)?.showCorrectAnswer ?? true;

    const isLocked = disabled || isReview || (showFeedback && !!result);

    // Build item lookup
    const itemsById = React.useMemo(
        () => new Map(items.map((item) => [item.id, item])),
        [items],
    );

    // Randomize items on first render, or restore from answer
    const [displayOrder, setDisplayOrder] = React.useState<string[]>(() => {
        if (answer && Array.isArray(answer) && answer.length === items.length) {
            return answer;
        }
        // Randomize the item order for the player
        const shuffled = shuffleArray([...items]);
        return shuffled.map((item) => item.id);
    });

    // Keep display order in sync when items change
    React.useEffect(() => {
        const itemIds = new Set(items.map((i) => i.id));
        setDisplayOrder((prev) => {
            // Filter out stale ids
            const filtered = prev.filter((id) => itemIds.has(id));
            // Add any new ids
            const existing = new Set(filtered);
            const newIds = items.filter((i) => !existing.has(i.id)).map((i) => i.id);
            if (newIds.length === 0 && filtered.length === prev.length) {
                return prev;
            }
            return [...filtered, ...newIds];
        });
    }, [items]);

    // NOTE: onAnswer is emitted ONLY from real learner interaction (handleDragEnd).
    // The initial (shuffled) order is not an answer: untouched => "not answered".

    const [activeDragId, setActiveDragId] = React.useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const correctOrder = React.useMemo(() => getCorrectOrder(orderingData), [orderingData]);

    const getItemState = (itemId: string, position: number): 'default' | 'correct' | 'incorrect' => {
        // ELMS-2370: hide answer => no per-item right/wrong marks.
        if (!showCorrectAnswer || !showFeedback || !result) {
            return 'default';
        }
        const correctId = correctOrder[position];
        return itemId === correctId ? 'correct' : 'incorrect';
    };

    const getCorrectPosition = (itemId: string): number | undefined => {
        const idx = correctOrder.indexOf(itemId);
        return idx >= 0 ? idx + 1 : undefined;
    };

    const handleDragStart = (event: DragStartEvent) => {
        setActiveDragId(String(event.active.id));
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragId(null);

        if (!over || active.id === over.id || isLocked) return;

        const oldIndex = displayOrder.indexOf(String(active.id));
        const newIndex = displayOrder.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0) return;
        const next = arrayMove(displayOrder, oldIndex, newIndex);
        setDisplayOrder(next);
        onAnswer?.(next);
    };

    const handleDragCancel = () => {
        setActiveDragId(null);
    };

    const activeItem = activeDragId ? itemsById.get(activeDragId) : undefined;

    if (!data || items.length === 0) {
        return (
            <div className={cn('text-muted-foreground italic p-4', className)}>
                {t('orderingPlayerNoData')}
            </div>
        );
    }

    return (
        <div className={cn('ordering-player space-y-4', className)}>
            {/* Question */}
            {question && (
                <div className="ordering-player-question">
                    <RichTextInline text={question} as="p" className="text-base font-medium text-foreground" />
                </div>
            )}

            {/* Sortable items list */}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <SortableContext
                    items={displayOrder}
                    strategy={verticalListSortingStrategy}
                >
                    <div className="ordering-player-items space-y-2">
                        {displayOrder.map((itemId, index) => {
                            const item = itemsById.get(itemId);
                            if (!item) return null;
                            return (
                                <SortableOrderingItem
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    disabled={isLocked}
                                    itemState={getItemState(item.id, index)}
                                    correctPosition={getCorrectPosition(item.id)}
                                    showCorrectAnswer={showCorrectAnswer}
                                    t={t}
                                />
                            );
                        })}
                    </div>
                </SortableContext>

                {/* Drag overlay for smooth animation */}
                <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
                    {activeItem ? (
                        <div className="ordering-player-drag-overlay flex items-center gap-3 rounded-lg border border-primary/60 bg-background shadow-xl p-3 opacity-90 pointer-events-none">
                            <GripVertical className="h-5 w-5 text-muted-foreground shrink-0" />
                            <Badge
                                variant="secondary"
                                className="bg-muted text-muted-foreground shrink-0 w-7 justify-center font-semibold"
                            >
                                ·
                            </Badge>
                            <span className="flex-1 text-sm font-medium text-foreground">
                                <RichTextInline text={activeItem.text} />
                            </span>
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* Explanation (after grading) */}
            {showFeedback && result && showCorrectAnswer && orderingData.explanation && (
                <div className="ordering-player-explanation mt-4 p-4 rounded-lg bg-info/10">
                    <h4 className="ordering-player-explanation-title font-medium mb-1">{t('explanation')}</h4>
                    <p className="ordering-player-explanation-body text-sm text-muted-foreground">
                        {orderingData.explanation}
                    </p>
                </div>
            )}
        </div>
    );
};

export default OrderingPlayer;
